import React, { useState } from 'react';
import { openClassBuddySplitScreen } from '../../utils/windowManager';

const SplitScreenButton = ({
    variant = 'floating',
    text = 'Open Split Screen',
    className = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showHelper, setShowHelper] = useState(false);

    const handleOpenSplitScreen = () => {
        const win = openClassBuddySplitScreen(true);
        if (win) {
            setIsOpen(true);
            setShowHelper(true);

            // Auto-hide helper after 8 seconds
            setTimeout(() => {
                setShowHelper(false);
            }, 8000);

            // Check if window is closed periodically
            const checkWindow = setInterval(() => {
                if (win.closed) {
                    setIsOpen(false);
                    clearInterval(checkWindow);
                }
            }, 1000);
        }
    };

    if (variant === 'floating') {
        return (
            <>
                {/* Helper Notification */}
                {showHelper && (
                    <div className="fixed top-20 right-4 z-50 bg-blue-600 text-white px-6 py-4 rounded-lg shadow-2xl max-w-sm animate-slide-in">
                        <div className="flex items-start gap-3">
                            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="flex-1">
                                <p className="font-semibold mb-1">💡 Split Screen Tip</p>
                                <p className="text-sm">Resize this lab window to the <strong>right half</strong> of your screen for the best side-by-side experience!</p>
                            </div>
                            <button
                                onClick={() => setShowHelper(false)}
                                className="text-white hover:text-gray-200 transition-colors"
                                aria-label="Close notification"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                <button
                    onClick={handleOpenSplitScreen}
                    className={`fixed right-3 sm:right-6 bottom-3 sm:bottom-6 z-50 ${isOpen
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-purple-700 hover:bg-purple-800'
                        } text-white px-4 sm:px-6 py-3 sm:py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 font-semibold flex items-center gap-2 sm:gap-3 group text-xs sm:text-base ${className}`}
                    aria-label={isOpen ? 'ClassBuddy Split Screen Active' : 'Open ClassBuddy Split Screen'}
                    title={isOpen ? 'ClassBuddy is open in split screen' : 'Open ClassBuddy in split screen mode'}
                >
                    {isOpen ? (
                        <>
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="hidden md:inline">Split Screen Active</span>
                            <span className="inline md:hidden">Active</span>
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                            </svg>
                            <span className="hidden md:inline">{text}</span>
                            <span className="inline md:hidden">Split</span>
                        </>
                    )}
                </button>
            </>
        );
    }

    // Inline button variant
    return (
        <button
            onClick={handleOpenSplitScreen}
            className={`inline-flex items-center gap-2 ${isOpen
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-purple-700 hover:bg-purple-800'
                } text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${className}`}
            aria-label={isOpen ? 'ClassBuddy Split Screen Active' : 'Open ClassBuddy Split Screen'}
        >
            {isOpen ? (
                <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Split Screen Active
                </>
            ) : (
                <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    {text}
                </>
            )}
        </button>
    );
};

export default SplitScreenButton;
